import type { ImportRecord, Item, Room, Section } from '../db/schema';
import type { Backup, Counts, ImageMeta } from './format';

/**
 * What restoring a file would do, worked out before anything is written.
 *
 * Pure, and separate from `restore.ts`, because this is the part that can
 * silently corrupt a library — a wrong answer here strands a photo or drops an
 * item into the wrong room — and it's the part worth testing.
 */

export type Mode = 'merge' | 'replace';

/** What's already here. Only the fields the plan actually reasons about. */
export type Current = {
  rooms: readonly Room[];
  sections: readonly Section[];
  items: readonly Item[];
  imports: readonly ImportRecord[];
  imageIds: readonly string[];
};

export type Plan = {
  mode: Mode;
  rooms: Room[];
  sections: Section[];
  items: Item[];
  imports: ImportRecord[];
  /** The images to pull out of the zip, with the names they were written as. */
  images: ImageMeta[];
  /** New rows only — what the summary afterwards reports. */
  added: Counts;
  /** Items in the file whose room is in neither the file nor the database. */
  orphaned: number;
};

export const EMPTY: Current = { rooms: [], sections: [], items: [], imports: [], imageIds: [] };

/**
 * Merge keys on id and never overwrites. Re-importing your own backup is a
 * no-op, which is the point; restoring an old one alongside a newer library
 * puts back what was deleted and leaves everything else alone. Overwriting
 * would quietly undo every edit made since the export, and there is no undo.
 */
export function planRestore(backup: Backup, current: Current, mode: Mode): Plan {
  // Replace writes the file over an empty database, so it plans against one.
  const here = mode === 'replace' ? EMPTY : current;

  const roomIds = new Set(here.rooms.map((room) => room.id));
  const sectionIds = new Set(here.sections.map((section) => section.id));
  const itemIds = new Set(here.items.map((item) => item.id));
  const importIds = new Set(here.imports.map((record) => record.id));
  const imageIds = new Set(here.imageIds);

  // Every image the file carries, whether or not anything still points at it.
  const inFile = new Map(backup.images.map((image) => [image.id, image]));
  const resolvable = (id: string | undefined): id is string =>
    id !== undefined && (imageIds.has(id) || inFile.has(id));

  // Incoming rows keep their order relative to each other and land above
  // everything already in that room, so a merge appends rather than shuffling
  // two arrangements that were never meant to meet.
  const roomShift = shift(here.rooms);
  const shifts = shiftsPerRoom(here);
  const shiftFor = (roomId: string) => shifts.get(roomId) ?? 0;

  const rooms = backup.rooms
    .filter((room) => !roomIds.has(room.id))
    .map((room) => ({
      ...room,
      coverImageId: resolvable(room.coverImageId) ? room.coverImageId : undefined,
      sort: room.sort + roomShift,
    }));

  const sections = backup.sections
    .filter((section) => !sectionIds.has(section.id))
    .map((section) => ({
      ...section,
      coverImageId: resolvable(section.coverImageId) ? section.coverImageId : undefined,
      sort: section.sort + shiftFor(section.roomId),
    }));

  // A section that arrived with the file is as good as one already here.
  const placeable = new Set([...sectionIds, ...sections.map((section) => section.id)]);
  const knownRooms = new Set([...roomIds, ...backup.rooms.map((room) => room.id)]);

  const incoming = backup.items.filter((item) => !itemIds.has(item.id));
  const items = incoming
    .filter((item) => knownRooms.has(item.roomId))
    .map((item) => ({
      ...item,
      // A section that didn't survive means the room's top level, not a
      // reference to nothing.
      sectionId:
        item.sectionId !== undefined && placeable.has(item.sectionId) ? item.sectionId : undefined,
      coverImageId: resolvable(item.coverImageId) ? item.coverImageId : undefined,
      imageIds: item.imageIds.filter(resolvable),
      sort: item.sort + shiftFor(item.roomId),
    }));

  const kept = new Set([...itemIds, ...items.map((item) => item.id)]);
  const imports = backup.imports.filter(
    (record) => !importIds.has(record.id) && kept.has(record.itemId),
  );
  const images = backup.images.filter((image) => !imageIds.has(image.id));

  return {
    mode,
    rooms,
    sections,
    items,
    imports,
    images,
    added: {
      rooms: rooms.length,
      sections: sections.length,
      items: items.length,
      images: images.length,
      imports: imports.length,
    },
    orphaned: incoming.length - items.length,
  };
}

/** One past the highest sort already in use. Zero when nothing is. */
function shift(rows: readonly { sort: number }[]): number {
  return rows.reduce((top, row) => Math.max(top, row.sort + 1), 0);
}

/**
 * Items and sections share one run of sort values per container, so the offset
 * has to count both. It's taken per room rather than per container: a coarser
 * number than strictly needed, and one that can't disturb anything already
 * arranged.
 */
function shiftsPerRoom(current: Current): Map<string, number> {
  const rows = new Map<string, { sort: number }[]>();

  for (const row of [...current.items, ...current.sections]) {
    const list = rows.get(row.roomId);
    if (list === undefined) rows.set(row.roomId, [row]);
    else list.push(row);
  }

  return new Map([...rows].map(([roomId, list]) => [roomId, shift(list)]));
}
